import { ViewType, ViewObject } from '../base';

import { deviceService } from '@unpispas/upp-base';
import { WSConnection } from '@unpispas/upp-base';

import { dataService } from '../../data';
import { Rasppi } from '../objects/rasppi';
import { RasppiConnect } from '../objects/rasppiconnect';
import { PosDevice } from '../objects/device';
import { PrintDevice } from '../objects/printer';
import { ScaleDevice } from '../objects/scale';
import { CodescanDevice } from '../objects/codescan';
import { Pinpad } from '../objects/pinpad';
import { Till } from '../objects/till';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _RasppiView extends ViewObject<Rasppi> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get rasppi(): Rasppi {
        return this.object;
    }

    constructor(object: Rasppi, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION       */
    /**********************************/

    private _validprinters: PrintDevice[] | null = null;
    private _validscales: ScaleDevice[] | null = null;
    private _validcodescans: CodescanDevice[] | null = null;
    private _validpinpads: Pinpad[] | null = null;
    private _validdevices: Array<PrintDevice | ScaleDevice | CodescanDevice> | null = null;
    private _validconnections: RasppiConnect[] | null = null;
    private _validtill: Till | null = null;

    private RefreshView(targets: string[]): void {
        let _dorefresh = false;

        if (targets.includes('RASPPI')){
            this._validprinters = null;
            this._validscales = null;
            this._validcodescans = null;
            this._validpinpads = null;
            this._validdevices = null;
            this._validtill = null;
            this._validconnections = null;
            _dorefresh = true;
        }

        if (targets.includes('TILL')){
            this._validtill = null;
            _dorefresh = true;
        }

        if (targets.includes('PRINTER')){
            this._validprinters = null;
            this._validdevices = null;
            _dorefresh = true;
        }

        if (targets.includes('SCALE')){
            this._validscales = null;
            this._validdevices = null;
            _dorefresh = true;
        }

        if (targets.includes('CODESCAN')){
            this._validcodescans = null;
            this._validdevices = null;
            _dorefresh = true;
        }

        if (targets.includes('PINPAD')){
            this._validpinpads = null;
            _dorefresh = true;
        }

        if (targets.includes('RASPPICONNECT')){
            this._validtill = null;
            this._validconnections = null;
            _dorefresh = true;
        }

        if (_dorefresh){
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void {
        const weakref = new WeakRef(this);

        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        this._subscriptions.push(
            this.rasppi.OnChildChanged.subscribe(
            () => {
                const self = weakref.deref();
                if (self){
                    self.doRegister();
                }
            })
        );

        this._subscriptions.push(
            this.rasppi.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){
                    self.RefreshView(targets);
                }
            })
        );

        for (const _printer of this.rasppi.printers){
            this._subscriptions.push(
                _printer.OnRefresh.subscribe(
                () => {
                    const self = weakref.deref();
                    if (self){
                        self.RefreshView(['PRINTER']);
                    }
                })
            );
        }

        for (const _scale of this.rasppi.scales){
            this._subscriptions.push(
                _scale.OnRefresh.subscribe(
                () => {
                    const self = weakref.deref();
                    if (self){
                        self.RefreshView(['SCALE']);
                    }
                })
            );
        }

        for (const _scanner of this.rasppi.codescans){
            this._subscriptions.push(
                _scanner.OnRefresh.subscribe(
                () => {
                    const self = weakref.deref();
                    if (self){
                        self.RefreshView(['CODESCAN']);
                    }
                })
            );
        }

        for (const _terminal of this.rasppi.pinpads){
            this._subscriptions.push(
                _terminal.OnRefresh.subscribe(
                () => {
                    const self = weakref.deref();
                    if (self){
                        self.RefreshView(['PINPAD']);
                    }
                })
            );
        }

        for (const _connect of this.rasppi.connects){
            this._subscriptions.push(
                _connect.OnRefresh.subscribe(
                () => {
                    const self = weakref.deref();
                    if (self){
                        self.RefreshView(['RASPPICONNECT']);
                    }
                })
            );
        }

        for (const _till of this.rasppi.tills || []){
            this._subscriptions.push(
                _till.OnRefresh.subscribe(
                () => {
                    const self = weakref.deref();
                    if (self){
                        self.RefreshView(['TILL']);
                    }
                })
            );
        }
    }

    /**********************************/
    /* CONNECTED HARDWARE             */
    /**********************************/

    get printers(): PrintDevice[] {
        if (this._validprinters === null){
            this._validprinters = this.rasppi.printers.filter((item: PrintDevice) => item.IsValid);
        }

        return this._validprinters;
    }

    get scales(): ScaleDevice[] {
        if (this._validscales === null){
            this._validscales = this.rasppi.scales.filter((item: ScaleDevice) => item.IsValid);
        }

        return this._validscales;
    }

    get codescans(): CodescanDevice[] {
        if (this._validcodescans === null){
            this._validcodescans = this.rasppi.codescans.filter((item: CodescanDevice) => item.IsValid);
        }

        return this._validcodescans;
    }

    /** Active payment terminals; newest `created` first (RF-PTM-03; not part of `devices`). */
    get pinpads(): Pinpad[] {
        if (this._validpinpads === null){
            this._validpinpads = this.rasppi.pinpads
                .filter((item: Pinpad) => item.IsValid)
                .sort((a, b) => {
                    const _acreated = (a.created || a.updated || new Date(0)).getTime();
                    const _bcreated = (b.created || b.updated || new Date(0)).getTime();
                    return _bcreated - _acreated;
                });
        }

        return this._validpinpads;
    }

    get devices(): Array<PrintDevice | ScaleDevice | CodescanDevice> {
        if (this._validdevices === null){
            this._validdevices = [...this.printers, ...this.scales, ...this.codescans];
        }

        return this._validdevices;
    }

    /**********************************/
    /* CONNECTED DEVICES              */
    /**********************************/

    get connected(): RasppiConnect[] {
        if (this._validconnections === null){
            this._validconnections = this.rasppi.connects.filter((item: RasppiConnect) => {
                return item.IsValid && item.hassvc && item.rasppi === this.rasppi;
            });
        }

        return this._validconnections;
    }

    /** First operational till linked to this service (`TILL.rasppi`). */
    get till(): Till | null {
        if (this._validtill === null){
            this._validtill = this.rasppi.till;
        }

        return this._validtill;
    }

    Connect(device: PosDevice, connect: string | null): RasppiConnect {
        if (!device){
            throw new Error('RasppiView.Connect requires a device');
        }

        if (!this.rasppi.place){
            throw new Error('RasppiView.Connect requires a place');
        }

        let _candidate: RasppiConnect | null = null;

        // keep only the active connect
        for (const _connect of device.connects ?? []){
            if (!_candidate){
                _candidate = _connect;
                if (_candidate.rasppi && (_candidate.rasppi !== this.rasppi)){
                    _candidate.rasppi.DelConnect(_candidate);
                }

                continue;
            }

            if (_connect.status !== 'DE'){
                _connect.status = 'DE';
            }
        }

        if (!_candidate){
            _candidate = new RasppiConnect(null, this.data);
            device.AddConnect(_candidate);
        }

        _candidate.status = 'AC';
        _candidate.rasppi = this.rasppi;
        _candidate.hassvc = true;
        _candidate.connect = connect;
        _candidate.device = device;

        this.rasppi.AddConnect(_candidate);

        return _candidate;
    }

    /**********************************/
    /* WEBSOCKET TRANSPORT            */
    /**********************************/

    /**
     * Active session connect for this rasppi: the valid connect whose device is
     * the current session device. Shared by `WSOpen`/`WSClose` so both resolve
     * the same `RasppiConnect` instance to open/close through.
     */
    private get session(): RasppiConnect | null {
        for (const _connect of this.rasppi.connects ?? []){
            if (_connect.IsValid && _connect.device?.IsCurrent){
                return _connect;
            }
        }

        return null;
    }

    /**
     * Opens a WebSocket to this service using the session connect configuration.
     * Resolves the active session connect for this rasppi, then delegates the
     * actual local/remote resolution and `devices.Connect(...)` call to
     * `RasppiConnect.Open` — the single path also used by `wsstoreService`.
     * @param devices Device service used to open the socket.
     * @param opts Optional open constraints. When `local` is true, rejects if the
     *   session would open a remote (gateway) connection instead of a local one.
     */
    async WSOpen(devices: deviceService, opts?: { local?: boolean }): Promise<WSConnection | null> {
        if (!devices){
            return null;
        }

        const _session = this.session;
        if (!_session){
            return null;
        }

        return _session.Open(devices, opts);
    }

    /**
     * Closes a WebSocket opened for this service, through the same session
     * connect `WSOpen` would resolve today (mirrors `Open`'s instance call
     * shape). Falls back to a direct `Disconnect` if the session connect is no
     * longer resolvable (e.g. device/session changed since open), so a socket
     * never leaks. Safe with null.
     */
    WSClose(connection: WSConnection | null): void {
        if (!connection){
            return;
        }

        const _session = this.session;
        if (_session){
            _session.Close(connection);
            return;
        }

        connection.Disconnect();
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type RasppiView = ViewType<_RasppiView, Rasppi>;

export function RasppiViewProxy(object: Rasppi, data: dataService): RasppiView {
    return new _RasppiView(object, data).Proxy as RasppiView;
}
